/**
 * The page-view path, and the one trap in building it.
 *
 * TanStack Router's location carries the query TWICE: `search` is a PARSED object — literally an
 * `Object.create(null)`, with no prototype and therefore no `toString` — and `searchStr` is the
 * raw string. Only the second one concatenates. Reaching for `search`, which is the obvious name,
 * makes `pathname + search` throw `Cannot convert object to primitive value` (bd10b29).
 *
 * In `PostHogPageView` that expression sits in a `useEffect` body OUTSIDE the effect's own
 * try/catch — the catch guards the analytics call, not the path — so the throw does not merely
 * lose a page view, it escapes into React from every navigation.
 *
 * This lives in its own module so the trap can be pinned by BEHAVIOUR. It was previously guarded
 * by `test/posthog-page-view.test.ts` regex-matching the component's source, which pinned where
 * the code lived rather than what it did: a behaviour-preserving destructuring refactor turned
 * that suite red (measured in 14-03), which is the same way a legitimate split in 13-01 was
 * forced to edit a pre-existing test.
 */

/**
 * The part of a router location this needs. `search` is declared, and deliberately as `unknown`,
 * so that reaching for the wrong field stays EXPRESSIBLE — a parameter shape that made the bug a
 * compile error would prove the compiler's defences, not this repo's.
 */
export interface PageViewLocation {
  readonly pathname: string;
  /** Parsed query object. Never concatenate this — see the module header. */
  readonly search?: unknown;
  /** Raw query string, e.g. `"?tab=month"`. Absent on a bare path. */
  readonly searchStr?: string;
}

/** `"/usage"` + `"?tab=month"` → `"/usage?tab=month"`; the query is part of the identity. */
export function pageViewPath(location: PageViewLocation): string {
  return location.pathname + (location.searchStr ?? "");
}
